import React, { useMemo, useState } from 'react';
import { useHabits } from '../hooks/useHabits';
import { Todo, TodoNavigationHabitCount } from '../types/data';
import Icon, { IconName } from './icons/Icon';
import { isShoppingHabitId } from '../utils/habits';
import IconButton from './IconButton';
import './TodoMenuHabitList.css';

interface TodoMenuHabitListProps {
  onHabitSelect?: (habitId: string) => void;
  selectedHabitId?: string | null;
  todos: Todo[];
  habitCounts?: TodoNavigationHabitCount[];
  /** 表示用の絞り込み文字列 */
  filterText?: string;
}

const TodoMenuHabitList: React.FC<TodoMenuHabitListProps> = ({
  onHabitSelect,
  selectedHabitId,
  todos,
  habitCounts,
  filterText = ''
}) => {
  const { activeHabits, reorderHabits } = useHabits();
  const [draggingHabitId, setDraggingHabitId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<{ id: string; insertAfter: boolean } | null>(null);

  const canReorder = (filterText || '').trim() === '';
  const habitCountById = useMemo(() => {
    const map = new Map<string, number>();
    habitCounts?.forEach(item => map.set(item.habit_id, item.open_count));
    return map;
  }, [habitCounts]);
  const filteredHabits = useMemo(() => {
    const q = (filterText || '').trim().toLowerCase();
    if (!q) return activeHabits;
    return activeHabits.filter(h => (h.title || '').toLowerCase().includes(q));
  }, [activeHabits, filterText]);

  // 「買い物」を常に先頭に優先表示
  const prioritizedHabits = useMemo(() => {
    const shopping = filteredHabits.find(h => isShoppingHabitId(h.id));
    const others = filteredHabits.filter(h => !isShoppingHabitId(h.id));
    return shopping ? [shopping, ...others] : others;
  }, [filteredHabits]);

  // カウント数を計算
  const getHabitCount = useMemo(() => {
    return (habitId: string): number => {
      try {
        if (habitCounts && habitCountById.has(habitId)) {
          return habitCountById.get(habitId) ?? 0;
        }
        return todos.filter(todo =>
          !todo.deleted && !todo.done && !todo.canceled &&
          todo.habitId === habitId
        ).length;
      } catch (error) {
        console.error('Failed to calculate habit count:', error);
        return 0;
      }
    };
  }, [todos, habitCounts, habitCountById]);

  const handleHabitClick = (habitId: string) => {
    onHabitSelect?.(habitId);
  };

  // 「買い物」習慣かどうかを判定
  // 習慣に応じたアイコン名を取得
  const getHabitIcon = (habitId: string): IconName => {
    return isShoppingHabitId(habitId) ? 'shop' : 'habit';
  };

  return (
    <div className="todo-habit-list">
      {/* 習慣一覧（絞り込み適用） */}
      {prioritizedHabits.map((habit) => (
        <div
          key={habit.id}
          className={[
            'todo-habit-item',
            selectedHabitId === habit.id ? 'selected' : '',
            draggingHabitId === habit.id ? 'dragging' : '',
            dragOver?.id === habit.id ? (dragOver.insertAfter ? 'drag-over-after' : 'drag-over-before') : '',
          ].filter(Boolean).join(' ')}
          onClick={() => handleHabitClick(habit.id)}
          role="button"
          draggable={canReorder && !isShoppingHabitId(habit.id)}
          aria-grabbed={draggingHabitId === habit.id}
          onDragStart={(e) => {
            if (!canReorder) return;
            if (isShoppingHabitId(habit.id)) return;
            e.dataTransfer.effectAllowed = 'move';
            setDraggingHabitId(habit.id);
          }}
          onDragEnd={() => {
            setDraggingHabitId(null);
            setDragOver(null);
          }}
          onDragOver={(e) => {
            if (!canReorder) return;
            if (!draggingHabitId) return;
            if (draggingHabitId === habit.id) return;
            if (isShoppingHabitId(habit.id)) return;
            e.preventDefault();
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const insertAfter = (e.clientY - rect.top) > rect.height / 2;
            setDragOver({ id: habit.id, insertAfter });
          }}
          onDragLeave={() => {
            if (dragOver?.id === habit.id) setDragOver(null);
          }}
          onDrop={async (e) => {
            if (!canReorder) return;
            if (!draggingHabitId) return;
            if (draggingHabitId === habit.id) return;
            if (isShoppingHabitId(habit.id)) return;
            e.preventDefault();
            const insertAfter = dragOver?.id === habit.id ? dragOver.insertAfter : false;
            setDragOver(null);
            try {
              await reorderHabits(draggingHabitId, habit.id, insertAfter);
            } finally {
              setDraggingHabitId(null);
            }
          }}
        >
          <div className='todo-habit-item-state-layer'>
            <div className="todo-habit-item-content">
              <div className="todo-habit-item-left">
                <Icon name={getHabitIcon(habit.id)} size={24} />
                <span className="todo-habit-title text-single-large">{habit.title}</span>
              </div>
              {(() => {
                const count = getHabitCount(habit.id);
                return count > 0 ? (
                  <span className="todo-habit-count text-single-large">{count}</span>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TodoMenuHabitList;
